import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  Router,
  ActivatedRoute,
  NavigationEnd,
  RouterModule,
} from '@angular/router';
import { Subscription, combineLatest, debounceTime, filter, take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { AppNotificationComponent } from '../notification/notification.component';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzDrawerComponent, NzDrawerModule } from 'ng-zorro-antd/drawer';
import { HeaderService } from '@/app/common/services/header/header.service';
import { NotificationCenterService } from '@/app/common/services/notification-center/notification-center.service';
import { NzInputModule } from 'ng-zorro-antd/input';
import {
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { AuthService } from '@/app/modules/auth/core/service';
import { LoaderService } from '@/app/common/services/loader/loader.service';
import { User } from '@/app/modules/user/core/type';
@Component({
  selector: 'az-blc-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    NzLayoutModule,
    NzIconModule,
    NzBadgeModule,
    NzPageHeaderModule,
    NzBreadCrumbModule,
    NzButtonModule,
    NzGridModule,
    NzDrawerModule,
    NzSpaceModule,
    NzDividerModule,
    NzPopoverModule,
    NzInputModule,
    CommonModule,
    RouterModule,
    MatIconModule,
    AppNotificationComponent,
    ReactiveFormsModule,
  ],
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  @ViewChild('drawer') drawer?: NzDrawerComponent;

  @ViewChild(AppNotificationComponent)
  notificationPanel?: AppNotificationComponent;

  @Output() onColapse = new EventEmitter();

  @Input() collapsed: boolean = false;

  @Input() isMobile: boolean = false;

  @Input() onlyName: boolean = false;

  breadcrumbs: Array<Breadcrumb> = [];

  title: string = '';

  subtitle: string = '';

  noticeCount = 0;

  user?: User;

  private subscription = new Subscription();

  form = new FormGroup({
    searchString: new FormControl<string>(''),
  });

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    public headerService: HeaderService,
    public loaderService: LoaderService,
    private notificationCenter: NotificationCenterService
  ) {}

  ngOnInit() {
    this.breadcrumbs = this.createBreadcrumbs(this.activatedRoute.root);

    this.listenBreadcrumb();

    this.listenForm();

    this.listenNotifications();

    this.loadData();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private loadUser() {
    return this.authService.authenticatedUser.pipe(take(1));
  }

  private loadData() {
    this.loaderService
      .executeWithLoader(combineLatest([this.loadUser()]))
      .subscribe({
        next: ([user]) => {
          this.user = user;
        },
        error: () => {
          delete this.user;
          this.authService.logout().subscribe();
        },
      });
  }

  logout() {
    this.loaderService.executeWithLoader(this.authService.logout()).subscribe({
      next: () => {
        this.router.navigateByUrl('/login');
      },
    });
  }

  private listenBreadcrumb() {
    this.subscription.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          this.breadcrumbs = this.createBreadcrumbs(this.activatedRoute.root);
        })
    );
  }

  private listenNotifications() {
    this.subscription.add(
      this.notificationCenter.unreadCount$.subscribe((count) => {
        this.noticeCount = count;
      })
    );
  }

  openNotificationDrawer() {
    this.drawer?.open();

    this.drawer?.afterOpen.pipe(take(1)).subscribe(() => {
      this.notificationPanel?.onDrawerOpen();
    });
  }

  onNotificationDrawerClose() {
    this.drawer?.close();
  }

  private listenForm() {
    this.subscription.add(
      this.form.controls.searchString.valueChanges
        .pipe(debounceTime(700))
        .subscribe((value) => {
          if (this.headerService.searchAction) {
            this.headerService.searchAction(value ?? '');
          }
        })
    );
  }

  private createBreadcrumbs(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: Array<Breadcrumb> = []
  ): Array<Breadcrumb> {
    const children: ActivatedRoute[] = route.children;
    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const childUrl = child.snapshot.url.map(segment => segment.path).join('/');
      if (childUrl) {
        const newUrl = `${url}/${childUrl}`;
        const label = child.snapshot.data['breadcrumb'];
        const icon = child.snapshot.data['breadcrumbIcon'];

        if (label && !breadcrumbs.some(b => b.url === newUrl)) {
          breadcrumbs.push({ label, url: newUrl, icon });
        }

        const title = child.snapshot.data['title'];
        if (title) {
          this.title = title;
        }

        const subtitle = child.snapshot.data['subtitle'];
        if (subtitle) {
          this.subtitle = subtitle;
        }
        this.createBreadcrumbs(child, newUrl, breadcrumbs);
      }
    }

    return breadcrumbs;
  }
}

interface Breadcrumb {
  label: string;
  url: string;
  icon?: string;
}
